import { useMemo, useState } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  SectionHeader,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createInsightsApi, type InsightRange } from "@/modules/dashboard/insights/api";
import {
  BarChart,
  CHART_COLORS,
  Funnel,
  HBars,
  Heatmap,
  Kpi,
  KpiGrid,
  Legend,
  LineChart,
  Note,
  RetentionTable,
  Stat,
  StatGrid,
  TwoCol,
} from "@/modules/dashboard/insights/charts";
import {
  formatHours,
  formatLabel,
  formatNumber,
  formatPercent,
  sumBy,
} from "@/modules/dashboard/insights/format";
import { InsightBoundary, RangeSelect } from "@/modules/dashboard/insights/parts";
import type { AnalyticsDto } from "@/modules/dashboard/insights/types";
import { useInsight } from "@/modules/dashboard/insights/useInsight";
import { SavedViewsHubPanel } from "./AnalyticsQueuePages";

/**
 * Analytics: how people use HUDDL over time, and why the numbers move.
 *
 * Dashboard answers "what is happening now and what needs a person".
 * Marketplace Health answers "are courts and customers meeting each other".
 * This page answers "how is behaviour changing" — trends, funnels, retention
 * and exploration over a range you choose.
 */
export function AnalyticsHubPage() {
  const client = useApiClient();
  const api = useMemo(() => createInsightsApi(client), [client]);
  const [days, setDays] = useState<InsightRange>(90);
  const [tab, setTab] = useState("trends");

  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Analytics" }]);

  const state = useInsight(() => api.analytics(days), [api, days]);
  const insightTab = tab !== "saved";

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Analytics"
        description="How people use HUDDL over time, and where they drop off. Pick a range, then look at trends, funnels, retention and who your members are."
        actions={
          insightTab ? (
            <Flex gap={12} align="flex-end" wrap>
              <RangeSelect value={days} onChange={setDays} id="analytics-range" />
              <Button variant="secondary" onClick={state.reload} loading={state.loading}>
                Refresh
              </Button>
            </Flex>
          ) : null
        }
      />

      <Alert tone="info" title="What is this page for?">
        Dashboard shows what is happening right now and what needs a person. Marketplace Health shows whether courts
        and customers are meeting each other. Analytics is for the question in between: is HUDDL growing, are people
        coming back, and where do they stop? Nothing here needs action; it is for understanding.
      </Alert>

      <Tabs
        value={tab}
        onValueChange={setTab}
        items={[
          {
            id: "trends",
            label: "Trends",
            content: (
              <InsightBoundary state={state} what="analytics">
                {(a) => <TrendsTab a={a} />}
              </InsightBoundary>
            ),
          },
          {
            id: "funnels",
            label: "Funnels",
            content: (
              <InsightBoundary state={state} what="analytics">
                {(a) => <FunnelsTab a={a} />}
              </InsightBoundary>
            ),
          },
          {
            id: "retention",
            label: "Retention",
            content: (
              <InsightBoundary state={state} what="analytics">
                {(a) => <RetentionTab a={a} />}
              </InsightBoundary>
            ),
          },
          {
            id: "audience",
            label: "Audience",
            content: (
              <InsightBoundary state={state} what="analytics">
                {(a) => <AudienceTab a={a} />}
              </InsightBoundary>
            ),
          },
          {
            id: "patterns",
            label: "Booking patterns",
            content: (
              <InsightBoundary state={state} what="analytics">
                {(a) => <PatternsTab a={a} days={days} />}
              </InsightBoundary>
            ),
          },
          { id: "saved", label: "Saved views and segments", content: <SavedViewsHubPanel /> },
        ]}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

function bucketLabel(a: AnalyticsDto): string {
  return a.bucket === "week" ? "week" : "day";
}

function TrendsTab({ a }: { a: AnalyticsDto }) {
  const stickiness = a.engagement.mau > 0 ? a.engagement.dau / a.engagement.mau : null;
  const signups = sumBy(a.signups_series, (p) => p.signups);
  const labels = a.signups_series.map((p) => p.date);
  const weekNote = a.bucket === "week" ? "Each bar or point is one week (starting Monday)." : "Each bar or point is one day.";

  return (
    <Stack gap={24}>
      <KpiGrid>
        <Kpi label="Members" value={formatNumber(a.engagement.members)} meta="Everyone with a Play profile" />
        <Kpi label={`New in the last ${a.window_days} days`} value={formatNumber(signups)} meta="By account creation time" />
        <Kpi label="Active today" value={formatNumber(a.engagement.dau)} meta="Did something in Play in the last 24 hours" />
        <Kpi label="Active this week" value={formatNumber(a.engagement.wau)} meta="Last 7 days" />
        <Kpi label="Active this month" value={formatNumber(a.engagement.mau)} meta="Last 30 days" />
        <Kpi
          label="Stickiness"
          value={formatPercent(stickiness)}
          meta="Active today, as a share of active this month. Higher means people come back more often."
        />
      </KpiGrid>
      <Note>
        "Active" means the member did something in Play: sent a chat message, posted, commented, reacted, uploaded a
        replay, joined a community or made a booking. Opening the app on its own is not tracked.
      </Note>

      <TwoCol>
        <Card header={<Text role="title">Sign-ups</Text>}>
          <Stack gap={8}>
            <LineChart
              labels={labels}
              series={[{ key: "signups", label: "Sign-ups", color: CHART_COLORS.primary, values: a.signups_series.map((p) => p.signups) }]}
              ariaLabel={`Sign-ups per ${bucketLabel(a)}`}
              emptyText="No sign-ups in this range"
            />
            <Note>{weekNote}</Note>
          </Stack>
        </Card>
        <Card header={<Text role="title">Active members</Text>}>
          <Stack gap={8}>
            <LineChart
              labels={a.active_series.map((p) => p.date)}
              series={[{ key: "active", label: "Active members", color: CHART_COLORS.accent, values: a.active_series.map((p) => p.active) }]}
              ariaLabel={`Distinct active members per ${bucketLabel(a)}`}
              emptyText="No member activity in this range"
            />
            <Note>Distinct members who did something in that {bucketLabel(a)}.</Note>
          </Stack>
        </Card>
      </TwoCol>

      <Card header={<Text role="title">What members are doing</Text>}>
        <Stack gap={8}>
          <BarChart
            stacked
            labels={a.content_series.map((p) => p.date)}
            series={[
              { key: "messages", label: "Chat messages", color: CHART_COLORS.primary, values: a.content_series.map((p) => p.messages) },
              { key: "posts", label: "Posts", color: CHART_COLORS.accent, values: a.content_series.map((p) => p.posts) },
              { key: "comments", label: "Comments", color: CHART_COLORS.info, values: a.content_series.map((p) => p.comments) },
              { key: "hypes", label: "Reactions", color: CHART_COLORS.warning, values: a.content_series.map((p) => p.hypes) },
              { key: "replays", label: "Replays", color: CHART_COLORS.danger, values: a.content_series.map((p) => p.replays) },
              { key: "bookings", label: "Bookings", color: CHART_COLORS.muted, values: a.content_series.map((p) => p.bookings) },
            ]}
            ariaLabel={`Member activity by type per ${bucketLabel(a)}`}
            emptyText="No member activity in this range"
          />
          <Legend
            series={[
              { key: "messages", label: "Chat messages", color: CHART_COLORS.primary },
              { key: "posts", label: "Posts", color: CHART_COLORS.accent },
              { key: "comments", label: "Comments", color: CHART_COLORS.info },
              { key: "hypes", label: "Reactions", color: CHART_COLORS.warning },
              { key: "replays", label: "Replays", color: CHART_COLORS.danger },
              { key: "bookings", label: "Bookings", color: CHART_COLORS.muted },
            ]}
          />
        </Stack>
      </Card>
    </Stack>
  );
}

function FunnelsTab({ a }: { a: AnalyticsDto }) {
  const ttfb = a.time_to_first_booking;
  return (
    <Stack gap={24}>
      <Note>
        A funnel follows the members who signed up in the chosen range and counts how many reached each next step. It
        shows where people stop. Each step includes only members who also completed the steps before it.
      </Note>

      <TwoCol>
        <Card header={<Text role="title">Sign-up to booking</Text>}>
          {a.funnel_booking.cohort_size === 0 ? (
            <EmptyState
              title="No sign-ups in this range"
              description="Choose a longer range, or wait for new members to sign up in HUDDL Play."
              reason="none"
            />
          ) : (
            <Funnel steps={a.funnel_booking.steps} />
          )}
        </Card>
        <Card header={<Text role="title">Sign-up to community engagement</Text>}>
          {a.funnel_community.cohort_size === 0 ? (
            <EmptyState
              title="No sign-ups in this range"
              description="Choose a longer range, or wait for new members to sign up in HUDDL Play."
              reason="none"
            />
          ) : (
            <Funnel steps={a.funnel_community.steps} />
          )}
        </Card>
      </TwoCol>

      <Card header={<Text role="title">Time from sign-up to first booking</Text>}>
        <Stack gap={12}>
          <StatGrid>
            <Stat label="Members who have booked" value={`${formatNumber(ttfb.members_with_booking)} of ${formatNumber(ttfb.members_total)}`} />
            <Stat label="Median time to first booking" value={formatHours(ttfb.median_hours)} />
            <Stat label="Slowest 10% take more than" value={formatHours(ttfb.p90_hours)} />
          </StatGrid>
          <Note>All members, not just the chosen range. "No data" means nobody has booked yet.</Note>
        </Stack>
      </Card>
    </Stack>
  );
}

function RetentionTab({ a }: { a: AnalyticsDto }) {
  return (
    <Stack gap={16}>
      <Note>
        Each row is the group of members who signed up in one week. The percentages show how many of them did something
        in Play in that week and in each week after. Darker means more came back. A steady middle column means people
        stay.
      </Note>
      <Card header={<Text role="title">Weekly cohorts, last 8 weeks</Text>}>
        <RetentionTable cohorts={a.retention} />
      </Card>
      <Note>
        Retention does not depend on the range selector. Very small cohorts swing wildly, so read a row with only a few
        members as a hint, not a result.
      </Note>
    </Stack>
  );
}

function AudienceTab({ a }: { a: AnalyticsDto }) {
  const skillsBySport = useMemo(() => {
    const grouped = new Map<string, { skill: string; members: number }[]>();
    for (const row of a.sport_skills) {
      const list = grouped.get(row.sport) ?? [];
      list.push({ skill: row.skill, members: row.members });
      grouped.set(row.sport, list);
    }
    return [...grouped.entries()].sort((x, y) => sumBy(y[1], (r) => r.members) - sumBy(x[1], (r) => r.members));
  }, [a.sport_skills]);

  return (
    <Stack gap={24}>
      <TwoCol>
        <Card header={<Text role="title">Sports members play</Text>}>
          <HBars
            empty="No member has chosen a sport yet. Members pick their sports during onboarding in HUDDL Play."
            items={a.sports.map((s) => ({ label: s.sport, value: s.members }))}
          />
        </Card>
        <Card header={<Text role="title">Skill level (leading sport)</Text>}>
          <HBars
            empty="No skill levels recorded yet."
            items={a.skills.map((s) => ({ label: s.skill, value: s.members }))}
          />
        </Card>
      </TwoCol>

      <Card header={<Text role="title">Skill level by sport</Text>}>
        {skillsBySport.length === 0 ? (
          <Note>No per-sport skill levels recorded yet. Members set one for each sport in onboarding or Edit profile.</Note>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <Thead>
                <Tr>
                  <Th>Sport</Th>
                  <Th>Skill levels</Th>
                </Tr>
              </Thead>
              <Tbody>
                {skillsBySport.map(([sport, rows]) => (
                  <Tr key={sport}>
                    <Td>{sport}</Td>
                    <Td>{rows.map((r) => `${r.skill}: ${r.members}`).join(" · ")}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Card>

      <TwoCol>
        <Card header={<Text role="title">Where members are</Text>}>
          <Stack gap={12}>
            {a.locations.top.length > 0 ? (
              <HBars items={a.locations.top.map((l) => ({ label: l.location, value: l.members }))} />
            ) : (
              <Note>
                No member has set a location. Location is an optional free-text field on the Play profile, so this stays
                empty until members fill it in. {formatNumber(a.locations.without_location)} of{" "}
                {formatNumber(a.locations.with_location + a.locations.without_location)} members have none.
              </Note>
            )}
            {a.locations.top.length > 0 ? (
              <Note>{formatNumber(a.locations.without_location)} members have not set a location.</Note>
            ) : null}
          </Stack>
        </Card>
        <Card header={<Text role="title">Home courts</Text>}>
          <HBars
            empty="No member has named a home court yet."
            items={a.home_courts.map((c) => ({ label: c.home_court, value: c.members }))}
          />
        </Card>
      </TwoCol>

      <Card header={<Text role="title">Markets</Text>}>
        {a.markets.length === 0 ? (
          <Note>No market has been set up.</Note>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Market</Th>
                <Th>Live businesses</Th>
                <Th>Bookings in range</Th>
              </Tr>
            </Thead>
            <Tbody>
              {a.markets.map((m) => (
                <Tr key={m.market}>
                  <Td>{m.market}</Td>
                  <Td>{formatNumber(m.businesses)}</Td>
                  <Td>{formatNumber(m.bookings)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}

function PatternsTab({ a, days }: { a: AnalyticsDto; days: number }) {
  const total = sumBy(a.booking_heatmap, (c) => c.n);
  return (
    <Stack gap={24}>
      <Card header={<Text role="title">When people play</Text>}>
        <Stack gap={8}>
          {total === 0 ? (
            <EmptyState
              title="No bookings in this range"
              description="Once players book, this shows which weekdays and hours they book most, in Manila time."
              reason="none"
            />
          ) : (
            <>
              <Heatmap cells={a.booking_heatmap} />
              <Note>
                Start times of the {formatNumber(total)} bookings created in the last {days} days that were not cancelled.
                Darker means more bookings start at that hour.
              </Note>
            </>
          )}
        </Stack>
      </Card>

      <TwoCol>
        <Card header={<Text role="title">How far ahead people book</Text>}>
          <HBars
            empty="No bookings in this range."
            items={a.booking_lead_time.map((b) => ({ label: b.bucket, value: b.n }))}
          />
        </Card>
        <Card header={<Text role="title">Where bookings come from</Text>}>
          <Stack gap={8}>
            <HBars
              empty="No bookings in this range."
              items={a.booking_sources.map((s) => ({ label: formatLabel(s.source), value: s.n }))}
            />
            <Note>
              "Unspecified" means the booking was made directly, with no community, creator or campaign attached.
            </Note>
          </Stack>
        </Card>
      </TwoCol>
    </Stack>
  );
}
